import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Plus, Trash2, Send, Phone, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppTab() {
  const [subscribers, setSubscribers] = useState([]);
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const token = localStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [subRes, statusRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/whatsapp/subscribers`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/whatsapp/status`, { headers }),
      ]);
      setSubscribers(subRes.data.subscribers);
      setStatus(statusRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addSubscriber = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/whatsapp/subscribers`, { phone, name }, { headers });
      setPhone(''); setName('');
      setMessage({ type: 'success', text: 'Subscriber ditambahkan!' });
      fetchData();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Gagal menambahkan' });
    }
  };

  const removeSubscriber = async (phone) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/whatsapp/subscribers/${encodeURIComponent(phone)}`, { headers });
      setMessage({ type: 'success', text: 'Subscriber dihapus' });
      fetchData();
    } catch (e) {
      setMessage({ type: 'error', text: 'Gagal menghapus' });
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/whatsapp/test`, { phone: testPhone }, { headers });
      setMessage({ type: res.data.status === 'sent' ? 'success' : 'error', text: res.data.status === 'sent' ? `Pesan terkirim! SID: ${res.data.sid}` : `Gagal: ${res.data.error}` });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Gagal mengirim' });
    }
    finally { setSending(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem', color: '#71717A' }}><Loader2 size={24} className="animate-spin" /></div>;

  return (
    <div data-testid="whatsapp-tab" style={{ maxWidth: '800px' }}>
      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', padding: '1rem', background: 'rgba(255,255,255,0.03)', borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.06)' }}>
        <MessageSquare size={20} color="#25D366" />
        <span style={{ color: '#EDEDED', fontWeight: 600 }}>WhatsApp Notifications</span>
        <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: status?.configured ? '#25D366' : '#f87171' }}>
          {status?.configured ? 'Terkonfigurasi' : 'Belum dikonfigurasi'}
        </span>
      </div>

      {/* Notification */}
      {message && (
        <div data-testid="wa-message" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', background: message.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(248,113,113,0.1)', border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.2)' : 'rgba(248,113,113,0.2)'}`, color: message.type === 'success' ? '#22c55e' : '#f87171', fontSize: '0.85rem' }}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
          <button onClick={() => setMessage(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>x</button>
        </div>
      )}

      {/* Sandbox Info */}
      <div style={{ padding: '1rem', marginBottom: '1.5rem', background: 'rgba(37,211,102,0.05)', border: '1px solid rgba(37,211,102,0.15)', borderRadius: '0.5rem', fontSize: '0.85rem', color: '#A1A1AA', lineHeight: 1.6 }}>
        <strong style={{ color: '#25D366' }}>Twilio Sandbox Mode</strong><br/>
        Penerima harus bergabung sandbox terlebih dahulu dengan mengirim pesan <strong style={{ color: '#EDEDED' }}>"join &lt;sandbox-code&gt;"</strong> ke nomor <strong style={{ color: '#EDEDED' }}>{status?.sandbox_number?.replace('whatsapp:', '') || '+14155238886'}</strong> via WhatsApp.
      </div>

      {/* Add Subscriber */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#EDEDED', fontSize: '1rem', marginBottom: '0.75rem' }}>Tambah Subscriber</h3>
        <form onSubmit={addSubscriber} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <input data-testid="wa-phone-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+628xxxxxxxxx" style={{ flex: 1, minWidth: '180px', padding: '0.6rem 0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.4rem', color: '#EDEDED', fontSize: '0.9rem' }} />
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Nama (opsional)" style={{ flex: 1, minWidth: '140px', padding: '0.6rem 0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.4rem', color: '#EDEDED', fontSize: '0.9rem' }} />
          <button data-testid="wa-add-btn" type="submit" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.2rem', background: '#25D366', color: '#fff', border: 'none', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
            <Plus size={16} /> Tambah
          </button>
        </form>
      </div>

      {/* Subscriber List */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: '#EDEDED', fontSize: '1rem', marginBottom: '0.75rem' }}>Subscribers ({subscribers.length})</h3>
        {subscribers.length === 0 ? (
          <p style={{ color: '#71717A', fontSize: '0.85rem' }}>Belum ada subscriber WhatsApp.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {subscribers.map(sub => (
              <div key={sub.phone} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.8rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '0.4rem' }}>
                <Phone size={14} color="#25D366" />
                <span style={{ color: '#EDEDED', fontSize: '0.9rem', fontFamily: 'monospace' }}>{sub.phone}</span>
                {sub.name && <span style={{ color: '#71717A', fontSize: '0.8rem' }}>{sub.name}</span>}
                <button data-testid={`wa-remove-${sub.phone}`} onClick={() => removeSubscriber(sub.phone)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '4px' }}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Test Message */}
      <div style={{ padding: '1rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '0.5rem' }}>
        <h3 style={{ color: '#EDEDED', fontSize: '1rem', marginBottom: '0.75rem' }}>Kirim Pesan Test</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input data-testid="wa-test-phone" value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="+628xxxxxxxxx" style={{ flex: 1, padding: '0.6rem 0.8rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.4rem', color: '#EDEDED', fontSize: '0.9rem' }} />
          <button data-testid="wa-test-btn" onClick={sendTest} disabled={sending} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.2rem', background: '#0D7377', color: '#fff', border: 'none', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', opacity: sending ? 0.6 : 1 }}>
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Test
          </button>
        </div>
      </div>
    </div>
  );
}
